import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MarkPaidResult, StatementDetail, StatementsResponse } from '@sparkery/contracts';
import { apiGet, apiPost } from './api.ts';

/**
 * Statements, from the browser's side.
 *
 * There is no `partnerId` parameter: the server scopes the list to the session. A
 * partner sees their own months; staff see everybody's. The client cannot ask for
 * anything else, which is why there is nothing here to get wrong.
 */

export function useStatements() {
  return useQuery({
    queryKey: ['statements'],
    queryFn: ({ signal }) => apiGet<StatementsResponse>('/statements', signal),
  });
}

/**
 * One statement and the movements behind it.
 *
 * Fetched only when a statement is actually opened. Folding the movements into the
 * list would mean every page load carried every row of every month, for a panel most
 * visits never open.
 */
export function useStatementDetail(statementId: string | undefined) {
  return useQuery({
    queryKey: ['statement-detail', statementId],
    queryFn: ({ signal }) => apiGet<StatementDetail>(`/statements/${statementId!}`, signal),
    enabled: Boolean(statementId),
  });
}

/**
 * Records a payment against a statement.
 *
 * The list is invalidated rather than patched: the response is one statement, and a
 * refetch is the only thing that keeps a staff list of many partners honest if somebody
 * else recorded a payment in the meantime.
 *
 * A 409 (already paid) surfaces as an error with the server's message, which is the
 * useful behaviour — the reference already on record is what the admin needs to see.
 */
export function useMarkStatementPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { statementId: string; reference: string }) =>
      apiPost<MarkPaidResult>(`/statements/${input.statementId}/paid`, {
        reference: input.reference,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['statements'] });
      // The open panel shows the statement's status and reference too, so it is now
      // stale — invalidated by prefix, since the panel is keyed by statement id.
      void queryClient.invalidateQueries({ queryKey: ['statement-detail'] });
    },
  });
}

/** `2026-10` → `October 2026`. The period is a label, never a date to parse. */
export function periodLabel(period: string): string {
  const [year, month] = period.split('-');
  const index = Number(month) - 1;
  const names = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return names[index] ? `${names[index]} ${year}` : period;
}
